/**
 * Logger Utility
 *
 * Provides controlled logging that automatically disables in production
 * to prevent sensitive information leakage and improve performance.
 *
 * Usage:
 * import logger from './utils/logger';
 *
 * logger.log('Debug info');
 * logger.error('Error occurred', error);
 * logger.warn('Warning message');
 * logger.info('Information');
 * logger.debug('Debug details');
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isTest = process.env.NODE_ENV === 'test';

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug' | 'trace';

interface LoggerConfig {
  enabled: boolean;
  levels: LogLevel[];
  timestamp: boolean;
}

const defaultConfig: LoggerConfig = {
  enabled: isDevelopment || isTest,
  levels: ['log', 'info', 'warn', 'error', 'debug'],
  timestamp: true,
};

class Logger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled && this.config.levels.includes(level);
  }

  private formatMessage(level: LogLevel, ...args: any[]): any[] {
    if (!this.config.timestamp) {
      return args;
    }
    const timestamp = new Date().toISOString();
    return [`[${timestamp}] [${level.toUpperCase()}]`, ...args];
  }

  log(...args: any[]): void {
    if (this.shouldLog('log')) {
      console.log(...this.formatMessage('log', ...args));
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      console.info(...this.formatMessage('info', ...args));
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...this.formatMessage('warn', ...args));
    }
  }

  error(...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(...this.formatMessage('error', ...args));
    }
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.debug(...this.formatMessage('debug', ...args));
    }
  }

  trace(...args: any[]): void {
    if (this.shouldLog('trace')) {
      console.trace(...this.formatMessage('trace', ...args));
    }
  }

  /**
   * Create a logger group
   */
  group(label?: string): void {
    if (this.config.enabled) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.config.enabled) {
      console.groupEnd();
    }
  }

  /**
   * Create a table output
   */
  table(data: any): void {
    if (this.config.enabled) {
      console.table(data);
    }
  }

  /**
   * Measure time
   */
  time(label: string): void {
    if (this.config.enabled) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.config.enabled) {
      console.timeEnd(label);
    }
  }

  /**
   * Clear console
   */
  clear(): void {
    if (this.config.enabled) {
      console.clear();
    }
  }

  /**
   * Assert condition
   */
  assert(condition: boolean, ...args: any[]): void {
    if (this.config.enabled) {
      console.assert(condition, ...args);
    }
  }
}

// Export singleton instance
const logger = new Logger();

export default logger;

// Export class for custom instances
export { Logger };

/**
 * Migration guide:
 *
 * 1. Replace direct console usage:
 *    Before: console.log('message')
 *    After:  logger.log('message')
 *
 * 2. Import the logger:
 *    import logger from '@/utils/logger';
 *
 * 3. Error handling:
 *    Before: console.error('Error:', error)
 *    After:  logger.error('Error:', error)
 *
 * 4. Debugging:
 *    Before: console.debug('Debug info')
 *    After:  logger.debug('Debug info')
 *
 * Benefits:
 * - Automatic disabling in production
 * - Consistent timestamp formatting
 * - Centralized logging control
 * - No sensitive data leakage
 * - Better performance in production
 */